import React from 'react';
import { FeatherX, FeatherFileText, FeatherAlertTriangle, FeatherCheckSquare, FeatherUsers, FeatherClipboard, FeatherTag, FeatherMessageSquare, FeatherCopy, FeatherTarget, FeatherCheckCircle, FeatherAlertCircle, FeatherClock } from '../ui/icons';
import './CopyNotesModal.css';

const NOTE_TYPES = {
    overallAssessment: {
        label: 'Overall Assessment',
        icon: <FeatherFileText size={18} />,
        color: '#3b82f6'
    },
    issues: {
        label: 'Issues/Concerns',
        icon: <FeatherAlertTriangle size={18} />,
        color: '#ef4444'
    },
    actionItems: {
        label: 'Action Items',
        icon: <FeatherCheckSquare size={18} />,
        color: '#10b981'
    },
    resourceObservations: {
        label: 'Resource Observations',
        icon: <FeatherUsers size={18} />,
        color: '#8b5cf6'
    },
    taskConcerns: {
        label: 'Task Concerns',
        icon: <FeatherClipboard size={18} />,
        color: '#f59e0b'
    },
    featureTagNotes: {
        label: 'Feature Tag Notes',
        icon: <FeatherTag size={18} />,
        color: '#ec4899'
    },
    additionalComments: {
        label: 'Additional Comments',
        icon: <FeatherMessageSquare size={18} />,
        color: '#06b6d4'
    }
};

const CopyNotesModal = ({ isOpen, onClose, onConfirm, sourceNotes, sourceDate, currentNotes }) => {
    if (!isOpen) return null;

    const hasCurrentNotes = currentNotes && Object.entries(currentNotes).some(([key, note]) => {
        if (key === 'goals') {
            // For goals, check if there are any goals set
            if (!note || typeof note !== 'object') return false;
            const goals = note;
            return (goals.tasks && goals.tasks.length > 0) || 
                   (goals.features && goals.features.length > 0) || 
                   (goals.personal && goals.personal.length > 0) ||
                   goals.noGoals === true;
        }
        // For other notes, check if it's a non-empty string
        return note && typeof note === 'string' && note.trim() !== '';
    });

    // Process goals: separate into "will be copied" and "will be skipped"
    const processGoals = () => {
        const goals = sourceNotes?.goals || {};
        const result = {
            tasks: { toCopy: [], toSkip: [] },
            features: { toCopy: [], toSkip: [] },
            personal: { toCopy: [], toSkip: [] },
            noGoals: goals.noGoals || false
        };

        // Process task goals
        (goals.tasks || []).forEach(goal => {
            if (goal.status === 'achieved') {
                result.tasks.toSkip.push(goal);
            } else {
                result.tasks.toCopy.push(goal);
            }
        });

        // Process feature goals
        (goals.features || []).forEach(goal => {
            if (goal.status === 'achieved') {
                result.features.toSkip.push(goal);
            } else {
                result.features.toCopy.push(goal);
            }
        });

        // Process personal goals
        (goals.personal || []).forEach(goal => {
            if (goal.status === 'achieved') {
                result.personal.toSkip.push(goal);
            } else {
                result.personal.toCopy.push(goal);
            }
        });

        return result;
    };

    const goalsData = processGoals();
    const hasGoals = goalsData.tasks.toCopy.length > 0 || goalsData.tasks.toSkip.length > 0 ||
                     goalsData.features.toCopy.length > 0 || goalsData.features.toSkip.length > 0 ||
                     goalsData.personal.toCopy.length > 0 || goalsData.personal.toSkip.length > 0 ||
                     goalsData.noGoals;

    const getStatusIcon = (status) => {
        switch (status) {
            case 'achieved':
                return <FeatherCheckCircle size={16} />;
            case 'not-achieved':
                return <FeatherAlertCircle size={16} />;
            case 'pending':
                return <FeatherClock size={16} />;
            default:
                return null;
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'achieved':
                return 'Achieved';
            case 'not-achieved':
                return 'Not Achieved';
            case 'pending':
                return 'Pending';
            default:
                return 'Unknown';
        }
    };

    const handleConfirm = () => {
        onConfirm(sourceNotes);
        onClose();
    };

    return (
        <div className="copy-notes-modal-overlay" onClick={onClose}>
            <div className="copy-notes-modal" onClick={(e) => e.stopPropagation()}>
                <div className="copy-notes-modal-header">
                    <div className="copy-notes-modal-header-content">
                        <div className="copy-notes-modal-icon">
                            <FeatherCopy size={20} />
                        </div>
                        <div>
                            <h2 className="copy-notes-modal-title">Copy Notes from Previous Review</h2>
                            <p className="copy-notes-modal-subtitle">
                                Source: {sourceDate ? new Date(sourceDate).toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                }) : 'Unknown'}
                            </p>
                        </div>
                    </div>
                    <button 
                        className="copy-notes-modal-close"
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        <FeatherX size={20} />
                    </button>
                </div>

                {hasCurrentNotes && (
                    <div className="copy-notes-modal-warning">
                        <FeatherAlertTriangle size={16} />
                        <span>Warning: This will replace your current notes.</span>
                    </div>
                )}

                <div className="copy-notes-modal-content">
                    <p className="copy-notes-modal-description">
                        Review the notes below. Click "Copy Notes" to copy them to today's review.
                    </p>
                    
                    <div className="copy-notes-modal-notes">
                        {Object.entries(NOTE_TYPES).map(([noteType, config]) => {
                            const noteContent = sourceNotes?.[noteType] || '';
                            if (!noteContent.trim()) return null;

                            return (
                                <div key={noteType} className="copy-notes-modal-note-card">
                                    <div className="copy-notes-modal-note-header" style={{ borderLeftColor: config.color }}>
                                        <div className="copy-notes-modal-note-icon" style={{ color: config.color }}>
                                            {config.icon}
                                        </div>
                                        <h3 className="copy-notes-modal-note-title">{config.label}</h3>
                                    </div>
                                    <div className="copy-notes-modal-note-content">
                                        <p className="copy-notes-modal-note-text">{noteContent}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Goals Section */}
                    {hasGoals && (
                        <div className="copy-notes-modal-goals-section">
                            <div className="copy-notes-modal-goals-header">
                                <div className="copy-notes-modal-goals-icon">
                                    <FeatherTarget size={20} />
                                </div>
                                <h3 className="copy-notes-modal-goals-title">Manager Goals</h3>
                            </div>
                            <p className="copy-notes-modal-goals-description">
                                Achieved goals will be automatically excluded when copying.
                            </p>

                            {/* Task Goals */}
                            {(goalsData.tasks.toCopy.length > 0 || goalsData.tasks.toSkip.length > 0) && (
                                <div className="copy-notes-modal-goals-category">
                                    <h4 className="copy-notes-modal-goals-category-title">
                                        Task Goals
                                        {goalsData.tasks.toCopy.length > 0 && (
                                            <span className="copy-notes-modal-goals-summary">
                                                ({goalsData.tasks.toCopy.length} will be copied
                                                {goalsData.tasks.toSkip.length > 0 && `, ${goalsData.tasks.toSkip.length} achieved goals skipped`})
                                            </span>
                                        )}
                                    </h4>
                                    <div className="copy-notes-modal-goals-list">
                                        {goalsData.tasks.toCopy.map((goal, index) => (
                                            <div key={goal.taskId || index} className="copy-notes-modal-goal-item copy-notes-modal-goal-to-copy">
                                                <div className="copy-notes-modal-goal-status-icon">
                                                    {getStatusIcon(goal.status || 'not-achieved')}
                                                </div>
                                                <div className="copy-notes-modal-goal-content">
                                                    <div className="copy-notes-modal-goal-name">[{goal.displayId}] {goal.taskTitle}</div>
                                                    <div className="copy-notes-modal-goal-status">{getStatusLabel(goal.status || 'not-achieved')}</div>
                                                </div>
                                                <span className="copy-notes-modal-goal-badge copy-notes-modal-goal-badge-copy">Will Copy</span>
                                            </div>
                                        ))}
                                        {goalsData.tasks.toSkip.map((goal, index) => (
                                            <div key={goal.taskId || index} className="copy-notes-modal-goal-item copy-notes-modal-goal-to-skip">
                                                <div className="copy-notes-modal-goal-status-icon">
                                                    {getStatusIcon(goal.status)}
                                                </div>
                                                <div className="copy-notes-modal-goal-content">
                                                    <div className="copy-notes-modal-goal-name">[{goal.displayId}] {goal.taskTitle}</div>
                                                    <div className="copy-notes-modal-goal-status">{getStatusLabel(goal.status)}</div>
                                                </div>
                                                <span className="copy-notes-modal-goal-badge copy-notes-modal-goal-badge-skip">Skipped</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Feature Goals */}
                            {(goalsData.features.toCopy.length > 0 || goalsData.features.toSkip.length > 0) && (
                                <div className="copy-notes-modal-goals-category">
                                    <h4 className="copy-notes-modal-goals-category-title">
                                        Feature Goals
                                        {goalsData.features.toCopy.length > 0 && (
                                            <span className="copy-notes-modal-goals-summary">
                                                ({goalsData.features.toCopy.length} will be copied
                                                {goalsData.features.toSkip.length > 0 && `, ${goalsData.features.toSkip.length} achieved goals skipped`})
                                            </span>
                                        )}
                                    </h4>
                                    <div className="copy-notes-modal-goals-list">
                                        {goalsData.features.toCopy.map((goal, index) => (
                                            <div key={goal.featureId || index} className="copy-notes-modal-goal-item copy-notes-modal-goal-to-copy">
                                                <div className="copy-notes-modal-goal-status-icon">
                                                    {getStatusIcon(goal.status || 'not-achieved')}
                                                </div>
                                                <div className="copy-notes-modal-goal-content">
                                                    <div className="copy-notes-modal-goal-name">{goal.featureName}</div>
                                                    <div className="copy-notes-modal-goal-status">{getStatusLabel(goal.status || 'not-achieved')}</div>
                                                </div>
                                                <span className="copy-notes-modal-goal-badge copy-notes-modal-goal-badge-copy">Will Copy</span>
                                            </div>
                                        ))}
                                        {goalsData.features.toSkip.map((goal, index) => (
                                            <div key={goal.featureId || index} className="copy-notes-modal-goal-item copy-notes-modal-goal-to-skip">
                                                <div className="copy-notes-modal-goal-status-icon">
                                                    {getStatusIcon(goal.status)}
                                                </div>
                                                <div className="copy-notes-modal-goal-content">
                                                    <div className="copy-notes-modal-goal-name">{goal.featureName}</div>
                                                    <div className="copy-notes-modal-goal-status">{getStatusLabel(goal.status)}</div>
                                                </div>
                                                <span className="copy-notes-modal-goal-badge copy-notes-modal-goal-badge-skip">Skipped</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Personal Goals */}
                            {(goalsData.personal.toCopy.length > 0 || goalsData.personal.toSkip.length > 0) && (
                                <div className="copy-notes-modal-goals-category">
                                    <h4 className="copy-notes-modal-goals-category-title">
                                        Personal Goals
                                        {goalsData.personal.toCopy.length > 0 && (
                                            <span className="copy-notes-modal-goals-summary">
                                                ({goalsData.personal.toCopy.length} will be copied
                                                {goalsData.personal.toSkip.length > 0 && `, ${goalsData.personal.toSkip.length} achieved goals skipped`})
                                            </span>
                                        )}
                                    </h4>
                                    <div className="copy-notes-modal-goals-list">
                                        {goalsData.personal.toCopy.map((goal, index) => (
                                            <div key={goal.id || index} className="copy-notes-modal-goal-item copy-notes-modal-goal-to-copy">
                                                <div className="copy-notes-modal-goal-status-icon">
                                                    {getStatusIcon(goal.status || 'pending')}
                                                </div>
                                                <div className="copy-notes-modal-goal-content">
                                                    <div className="copy-notes-modal-goal-name">{goal.description}</div>
                                                    <div className="copy-notes-modal-goal-status">{getStatusLabel(goal.status || 'pending')}</div>
                                                </div>
                                                <span className="copy-notes-modal-goal-badge copy-notes-modal-goal-badge-copy">Will Copy</span>
                                            </div>
                                        ))}
                                        {goalsData.personal.toSkip.map((goal, index) => (
                                            <div key={goal.id || index} className="copy-notes-modal-goal-item copy-notes-modal-goal-to-skip">
                                                <div className="copy-notes-modal-goal-status-icon">
                                                    {getStatusIcon(goal.status)}
                                                </div>
                                                <div className="copy-notes-modal-goal-content">
                                                    <div className="copy-notes-modal-goal-name">{goal.description}</div>
                                                    <div className="copy-notes-modal-goal-status">{getStatusLabel(goal.status)}</div>
                                                </div>
                                                <span className="copy-notes-modal-goal-badge copy-notes-modal-goal-badge-skip">Skipped</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Goals Option */}
                            {goalsData.noGoals && (
                                <div className="copy-notes-modal-goals-no-goals">
                                    <FeatherCheckCircle size={16} />
                                    <span>No goals were set for this day</span>
                                </div>
                            )}
                        </div>
                    )}

                    {Object.values(NOTE_TYPES).every((_, index) => {
                        const noteType = Object.keys(NOTE_TYPES)[index];
                        const noteContent = sourceNotes?.[noteType] || '';
                        return !noteContent.trim();
                    }) && !hasGoals && (
                        <div className="copy-notes-modal-empty">
                            <p>No notes found in the source review.</p>
                        </div>
                    )}
                </div>

                <div className="copy-notes-modal-footer">
                    <button
                        className="copy-notes-modal-cancel"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        className="copy-notes-modal-confirm"
                        onClick={handleConfirm}
                    >
                        Copy Notes
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CopyNotesModal;

